'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { reporteTextoService, ReporteTextoParams } from '@/lib/api/reporteTextoService';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';
import { Solicitud } from '@/types/solicitud';
import { Empresa } from '@/types/empresa';
import { AgendaEntry } from '@/types/agenda';

type Catalogo = 'Solicitudes' | 'Empresas' | 'Agendas';

// Campos de solicitudes: mismos que legacy / modelo backend (alias camelCase + label legible)
const camposSolicitudes: Array<{ key: string; label: string }> = [
  { key: 'id', label: 'Id' },
  { key: 'solicitudNumeroControl', label: 'Solicitud Numero De Control' },
  { key: 'ownerID', label: 'Asesor Origen' },
  { key: 'inheritedID', label: 'Asesor Final Id' },
  { key: 'asesorNombre', label: 'Asesor (Nombre)' },
  { key: 'regionNombre', label: 'Region' },
  { key: 'producto', label: 'Producto' },
  { key: 'clienteEmpresa', label: 'Cliente Empresa' },
  { key: 'voboRequerido', label: 'Se Requiere VoBo' },
  { key: 'clienteTipoEmpresa', label: 'Cliente Tipo De Empresa' },
  { key: 'clienteNivelRiesgoEmpresa', label: 'Cliente Nivel Riesgo De Empresa' },
  { key: 'clientePagadora', label: 'Cliente Pagadora' },
  { key: 'clienteNombre', label: 'Cliente Nombre' },
  { key: 'clienteApellidoPaterno', label: 'Cliente Apellido Paterno' },
  { key: 'clienteApellidoMaterno', label: 'Cliente Apellido Materno' },
  { key: 'clienteNuevoRenovacion', label: 'Cliente Nuevo/Renovación' },
  { key: 'clientePuesto', label: 'Cliente Puesto' },
  { key: 'clienteDirectivo', label: 'Cliente Es Director' },
  { key: 'clienteSindicalizado', label: 'Cliente Sindicalizado' },
  { key: 'clienteAntiguedad', label: 'Cliente Antigüedad' },
  { key: 'clienteFechaIngreso', label: 'Cliente Fecha Fecha De Ingreso' },
  { key: 'clienteAntiguedadMeses', label: 'Cliente Antigüedad (Meses)' },
  { key: 'clienteClabe', label: 'Cliente Cuenta Clabe' },
  { key: 'frecuenciaDePago', label: 'Frecuencia De Pago' },
  { key: 'clienteSalario', label: 'Cliente Ingreso Bruto Mensual ($)' },
  { key: 'clienteDeduccionesSemanales', label: 'Cliente Deducciones Semanales ($)' },
  { key: 'clienteDeduccionesQuincenales', label: 'Cliente Deducciones Quincenales ($)' },
  { key: 'clienteDeduccionesMensuales', label: 'Cliente Deducciones Mensuales ($)' },
  { key: 'clienteDeduccionesPeriodo', label: 'Cliente Total Deducciones/Período($)' },
  { key: 'solicitudDestinoCredito', label: 'Solicitud Destino Del Crédito' },
  { key: 'montoSolicitado', label: 'Monto Solicitado ($)' },
  { key: 'plazoSolicitado', label: 'Plazo Solicitado En Meses' },
  { key: 'polizaSeguro', label: 'Poliza De Seguro' },
  { key: 'tipoPagoSeguro', label: 'Solicitud De Seguro' },
  { key: 'tipoListaNegra', label: 'Listas Negras Tipo' },
  { key: 'saldo1a89', label: 'Saldo De 1 - 89 Dias ($)' },
  { key: 'saldoMayor90', label: 'Saldo Con 90 - 120 Dias ($)' },
  { key: 'saldoMayor120', label: 'Saldo Mayor A 120 Dias($)' },
  { key: 'solicitudMop', label: 'Solicitud Mop Más Alto' },
  { key: 'clienteCorreo', label: 'Cliente Correo' },
  { key: 'clienteNombreJefe', label: 'Cliente Nombre De Jefe Inmediato' },
  { key: 'clienteCorreoJefe', label: 'Cliente Correo De Jefe Inmediato' },
  { key: 'clienteSeEnteroPor', label: 'Cliente Se Entero Por' },
  { key: 'solicitudEsCleanUp', label: 'Solicitud Es Clean Up' },
  { key: 'solicitudNumeroControlAnterior', label: 'Solicitud Número De Control Anterior' },
  { key: 'solicitudTasaTipoAnterior', label: 'Solicitud Buro De Credito (Sólo En Cn)' },
  { key: 'solicitudSaldoActualSIACAnterior', label: 'Solicitud Saldo Actual Cdto Anterior Siac $' },
  { key: 'solicitudTotalPagadoSIACAnterior', label: 'Solicitud Pagado Cdto Anterior %' },
  { key: 'solicitudSaldoParcialidadesNumeroAnterior', label: 'Solicitud Parcialidades Por Cobrar Siac #' },
  { key: 'solicitudSaldoParcialidadesMontoAnterior', label: 'Solicitud Parcialidades Por Cobrar Siac $' },
  { key: 'solicitudMontoOriginalAnterior', label: 'Solicitud Monto Original Anterior' },
  { key: 'solicitudAvanceCreditoAnterior', label: 'Solicitud Avance Credito Anterior' },
  { key: 'solicitudIncrementoSaldoActual', label: 'Solicitud Incremento Al Saldo Actual %' },
  { key: 'solicitudSaldoTotalAnterior', label: 'Solicitud Saldo Total Anterior' },
  { key: 'lineaDeCredito', label: 'Línea De Crédito ($)' },
  { key: 'clientePrestarHasta', label: 'Cliente Prestar Hasta (Meses)' },
  { key: 'clienteAvalRequerido', label: 'Cliente Aval Requerido' },
  { key: 'montoMaximoPolitica', label: 'Monto Máximo Política ($)' },
  { key: 'montoPropuesto', label: 'Monto Propuesto ($)' },
  { key: 'plazoMaximoPolitica', label: 'Plazo Máx Política En Meses' },
  { key: 'tipoBuroCredito', label: 'Buro De Credito' },
  { key: 'montoBuenBuro', label: 'Oferta Buen Buró ($)' },
  { key: 'tasaDeComisionPolitica', label: 'Tasa De Comision Politica' },
  { key: 'montoComisionPolitica', label: 'Monto Comisión Política($)' },
  { key: 'tasaPolitica', label: 'Tasa Politica' },
  { key: 'excepcionDocumentada1', label: 'Excepcion Documentada 1' },
  { key: 'excepcionDocumentada2', label: 'Excepcion Documentada 2' },
  { key: 'excepcionDocumentada3', label: 'Excepcion Documentada 3' },
  { key: 'montoAutorizado', label: 'Monto Autorizado ($)' },
  { key: 'plazoAutorizado', label: 'Plazo Autorizado En Meses' },
  { key: 'montoTransferencia', label: 'Monto Transferencia ($)' },
  { key: 'tasaDeComision', label: 'Tasa De Comision' },
  { key: 'tasaAutorizada', label: 'Tasa Autorizada' },
  { key: 'fechaEmisionContrato', label: 'Fecha Fecha De Emision De Contrato' },
  { key: 'fechaPrimerCobro', label: 'Fecha Fecha De Primer Cobro' },
  { key: 'errorTransferencia', label: 'Error Por Montos' },
  { key: 'montoComision', label: 'Monto Comision Autorizada($)' },
  { key: 'solicitudSeguroFinanciado', label: 'Solicitud Seguro Financiado' },
  { key: 'numeroPagos', label: 'Numero De Pagos' },
  { key: 'fechaVencimientoLineaDeCredito', label: 'Fecha Vencimiento De Línea De Cto' },
  { key: 'montoCapacidadDePago', label: 'Capacidad De Pago ($)' },
  { key: 'pagoEstimadoPeriodo', label: 'Pago Estimado Crédito ($)' },
  { key: 'costoSeguroParcialidad', label: 'Parcialidad Seguro ($)' },
  { key: 'pagoTotalPeriodo', label: 'Pago Total Estimado ($)' },
  { key: 'capacidadDePago', label: 'Capacidad De Pago' },
  { key: 'fechaContacto', label: 'Fecha Solicitud' },
  { key: 'fechaEntregaARiesgos', label: 'Fecha Entrega A Riesgos' },
  { key: 'fechaValidacionClabe', label: 'Fecha Validación Clabe' },
  { key: 'fechaRevisionDocumentalConluida', label: 'Fecha Revisión Documental Concluída' },
  { key: 'fechaAnalisisConluido', label: 'Fecha Análisis Concluído' },
  { key: 'fechaPrimerSolicitudVoBo', label: 'Fecha 1Er Solicitud Vobo' },
  { key: 'fechaSegundaSolicitudVoBo', label: 'Fecha 2Da Solicitud Vobo' },
  { key: 'fechaTercerSolicitudVoBo', label: 'Fecha 3Er Solicitud Vobo' },
  { key: 'fechaVoBo', label: 'Fecha Recibe Vobo' },
  { key: 'fechaPropuesta', label: 'Fecha Propuesta' },
  { key: 'fechaRechazoRiesgos', label: 'Fecha Rechazado Riesgos' },
  { key: 'motivoNoCierre', label: 'Solicitud Riesgos Rechazado Por' },
  { key: 'fechaRechazo', label: 'Fecha Perdido' },
  { key: 'autorizacionDG', label: 'Autorizacion De D.G.' },
  { key: 'fechaAutorizacionDG', label: 'Fecha Autorizado DG' },
  { key: 'fechaRechazoDG', label: 'Fecha Rechazado DG' },
  { key: 'fechaContratoImpreso', label: 'Fecha Contrato Impreso' },
  { key: 'fechaEntregaContratoFirmado', label: 'Fecha Contrato Firmado' },
  { key: 'contratoDocumento', label: 'Cumple El Contrato?' },
  { key: 'fechaFondeado', label: 'Fecha Fondeado' },
  { key: 'fechaCancelacionCliente', label: 'Fecha Cancelado Por Cliente' },
  { key: 'fechaCancelacionCartera', label: 'Fecha Cancelado Por Cartera' },
  { key: 'fechaReferenciaCobranza', label: 'Fecha En Cobranza' },
  { key: 'clienteReferenciaCobranza', label: 'Cliente Referencia Cobranza' },
  { key: 'solicitudEstatus', label: 'Solicitud Estatus' },
  { key: 'usuarioAutorizacionRiesgos', label: 'Autorizado En Riesgos Por' },
  { key: 'comentarios', label: 'Comentarios' },
  { key: 'fechaExpedienteRecibidoRiesgos', label: 'Fecha Expediente Entregado A Riesgos' },
  { key: 'fechaExpedienteRevisadoRiesgos', label: 'Fecha Expediente Revisado En Riesgos' },
  { key: 'fechaExpedienteDevueltoAComercial', label: 'Fecha Expediente Devuelto A Comercial' },
  { key: 'fechaExpedienteResguardadoEnBoveda', label: 'Fecha Expediente Resguardado En Boveda' },
  { key: 'usuarioExpedienteRecibidoRiesgos', label: 'Expediente Entregado A Riesgos Por' },
  { key: 'usuarioExpedienteRevisadoRiesgos', label: 'Expediente Revisado En Riesgos Por' },
  { key: 'usuarioExpedienteDevueltoAComercial', label: 'Expediente Devuelto A Comercial Por' },
  { key: 'usuarioExpedienteResguardadoEnBoveda', label: 'Expediente Resguardado En Boveda Por' },
  { key: 'expedienteResguardoStatus', label: 'Expediente Estatus Resguardo' },
  { key: 'comentariosResguardoExpedientes', label: 'Expediente Comentarios Resguardo' },
  { key: 'analistaNombre', label: 'Analisis Realizado Por' },
  { key: 'estatusAnalisis', label: 'Analisis Estatus' },
  { key: 'observacionesAnalisis', label: 'Observaciones Analisis' },
  { key: 'documentos', label: 'Archivos' },
  { key: 'documentosRoip', label: 'Link Documentos' },
  { key: 'etapaEmbudo', label: 'Etapa Embudo' },
  { key: 'estatusEmbudo', label: 'Estatus Embudo' },
  { key: 'tipoDeNegocio', label: 'Tipo De Negocio' },
  { key: 'autorizacionDirector', label: 'Autorizacion Director' },
  { key: 'clienteDeducciones', label: 'Cliente Deducciones' },
  { key: 'fechaUltimoPago', label: 'Fecha Ultimo Pago' },
  { key: 'tasaDelCredito', label: 'Tasa Del Credito' },
  { key: 'montoSeguro', label: 'Monto Seguro' },
  { key: 'montoIvaSeguro', label: 'Monto Iva Seguro' },
  { key: 'montoMinistracion', label: 'Monto Ministracion' },
  { key: 'montoIvaComision', label: 'Monto Iva Comision' },
  { key: 'pagoParcialidadSimulador', label: 'Pago Parcialidad Simulador' },
  { key: 'totalInteresesSimulador', label: 'Total Intereses Simulador' },
  { key: 'ivaInteresesSimulador', label: 'Iva Intereses Simulador' },
  { key: 'pagoTotalSimulador', label: 'Pago Total Simulador' },
  // Campos adicionales del modelo
  { key: 'buenBuroEstatus', label: 'Buen Buro Estatus' },
  { key: 'buenBuroMontoOferta', label: 'Buen Buro Monto Oferta' },
  { key: 'buenBuroMontoAceptado', label: 'Buen Buro Monto Aceptado' },
  { key: 'propuestaBuenBuro', label: 'Propuesta Buen Buro' },
  { key: 'propuestaSolicitud', label: 'Propuesta Solicitud' },
  { key: 'propuestaRecomendacion', label: 'Propuesta Recomendacion' },
  { key: 'revisionDocumental', label: 'Revision Documental' },
  { key: 'editable', label: 'Editable' },
];

// Campos de empresas: alineados con modelo backend y legacy (diccionarioCamposEmpresas)
const camposEmpresas: Array<{ key: string; label: string }> = [
  { key: 'id', label: 'Id' },
  { key: 'nombre', label: 'Nombre' },
  { key: 'agrupacion', label: 'Agrupación' },
  { key: 'empresaPagadora', label: 'Pagadora' },
  { key: 'empresaSegmentacionCobranza', label: 'Segmentación de Cobranza' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'region', label: 'Región' },
  { key: 'autorizacionDG', label: 'Autorizacion DG' },
  { key: 'voboRequerido', label: 'Se Requiere VoBo' },
  { key: 'estatus', label: 'Estatus' },
  { key: 'nivel', label: 'Nivel' },
];

// Campos de agendas: orden solicitado
const camposAgendas: Array<{ key: string; label: string }> = [
  { key: 'actividad', label: 'Actividad' },
  { key: 'comentarios', label: 'Comentarios' },
  { key: 'empresa', label: 'Empresa' },
  { key: 'fecha', label: 'Fecha' },
  { key: 'hora', label: 'Hora' },
  { key: 'clienteNombre', label: 'Nombre del cliente' },
  { key: 'clienteNuevoRenovacion', label: 'Tipo de cliente' },
  { key: 'ubicacion', label: 'Ubicacion' },
];

// Campo base para la fecha: mismas opciones que legacy (todas las fechas de solicitud)
const camposFechaSolicitudes: Array<{ key: string; label: string }> = [
  { key: '', label: '' },
  { key: 'fechaContacto', label: 'Fecha Contacto' },
  { key: 'fechaEntregaARiesgos', label: 'Fecha Entrega A Riesgos' },
  { key: 'fechaPropuesta', label: 'Fecha Propuesta' },
  { key: 'fechaPrimerSolicitudVoBo', label: 'Fecha Primer Solicitud VoBo' },
  { key: 'fechaSegundaSolicitudVoBo', label: 'Fecha Segunda Solicitud VoBo' },
  { key: 'fechaTercerSolicitudVoBo', label: 'Fecha Tercer Solicitud VoBo' },
  { key: 'fechaVoBo', label: 'Fecha VoBo' },
  { key: 'fechaRechazoRiesgos', label: 'Fecha Rechazo Riesgos' },
  { key: 'fechaContratoImpreso', label: 'Fecha Contrato Impreso' },
  { key: 'fechaAutorizacionDG', label: 'Fecha Autorizacion DG' },
  { key: 'fechaRechazoDG', label: 'Fecha Rechazo DG' },
  { key: 'fechaCancelacionCartera', label: 'Fecha Cancelacion Cartera' },
  { key: 'fechaEntregaContratoFirmado', label: 'Fecha Entrega Contrato Firmado' },
  { key: 'fechaFondeado', label: 'Fecha Fondeado' },
  { key: 'fechaCancelacionCliente', label: 'Fecha Cancelacion Cliente' },
  { key: 'fechaRechazo', label: 'Fecha Rechazo' },
  { key: 'fechaValidacionClabe', label: 'Fecha Validacion Clabe' },
  { key: 'fechaRevisionDocumentalConluida', label: 'Fecha Revision Documental Concluida' },
  { key: 'fechaAnalisisConluido', label: 'Fecha Analisis Concluido' },
  { key: 'fechaReferenciaCobranza', label: 'Fecha Referencia Cobranza' },
  { key: 'fechaEmisionContrato', label: 'Fecha Emision Contrato' },
  { key: 'fechaPrimerCobro', label: 'Fecha Primer Cobro' },
  { key: 'fechaUltimoPago', label: 'Fecha Ultimo Pago' },
  { key: 'fechaVencimientoLineaDeCredito', label: 'Fecha Vencimiento Linea De Credito' },
  { key: 'fechaExpedienteRecibidoRiesgos', label: 'Fecha Expediente Recibido Riesgos' },
  { key: 'fechaExpedienteRevisadoRiesgos', label: 'Fecha Expediente Revisado Riesgos' },
  { key: 'fechaExpedienteDevueltoAComercial', label: 'Fecha Expediente Devuelto A Comercial' },
  { key: 'fechaExpedienteResguardadoEnBoveda', label: 'Fecha Expediente Resguardado En Boveda' },
  { key: 'clienteFechaIngreso', label: 'Cliente Fecha Ingreso' },
];

const ReporteTextoManagement: React.FC = () => {
  const { user, isFieldAllowed } = useAuth();
  const [catalogo, setCatalogo] = useState<Catalogo>('Solicitudes');
  const [camposSeleccionados, setCamposSeleccionados] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Filtros de fecha
  const [fechaCriterio, setFechaCriterio] = useState('fechaContacto');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  
  // Filtros adicionales
  const [filtroCriterio1, setFiltroCriterio1] = useState('');
  const [filtroValor1, setFiltroValor1] = useState('');
  const [filtroCriterio2, setFiltroCriterio2] = useState('');
  const [filtroValor2, setFiltroValor2] = useState('');
  const [filtroCriterio3, setFiltroCriterio3] = useState('');
  const [filtroValor3, setFiltroValor3] = useState('');

  const camposDisponibles = useMemo(() => {
    const filterByAcl = (campos: Array<{ key: string; label: string }>) =>
      campos.filter((c) => {
        const aclKey = c.key === 'documentosRoip' ? 'documentosROIP' : c.key;
        return isFieldAllowed(aclKey);
      });
    switch (catalogo) {
      case 'Solicitudes':
        return filterByAcl(camposSolicitudes);
      case 'Empresas':
        return filterByAcl(camposEmpresas);
      case 'Agendas':
        return filterByAcl(camposAgendas);
      default:
        return [];
    }
  }, [catalogo, isFieldAllowed]);

  const camposFiltro = useMemo(() => {
    return camposDisponibles.map(c => ({ key: c.key, label: c.label }));
  }, [camposDisponibles]);

  const toggleCampo = useCallback((campoKey: string) => {
    setCamposSeleccionados(prev => {
      if (prev.includes(campoKey)) {
        return prev.filter(k => k !== campoKey);
      }
      return [...prev, campoKey];
    });
  }, []);

  const construirCamposSolicitados = useCallback((): string => {
    return camposSeleccionados
      .map(key => {
        const campo = camposDisponibles.find(c => c.key === key);
        return campo ? `${campo.key}:${campo.label}` : '';
      })
      .filter(Boolean)
      .join('|');
  }, [camposSeleccionados, camposDisponibles]);

  const generarReporte = useCallback(async () => {
    if (camposSeleccionados.length === 0) {
      setError('Selecciona al menos un campo');
      return;
    }

    if (!user?.sub) {
      setError('Usuario no autenticado');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params: ReporteTextoParams = {
        catalogo,
        camposSolicitados: construirCamposSolicitados(),
        ownerID: user.sub,
      };

      // Agregar filtros según el catálogo
      if (catalogo === 'Solicitudes') {
        if (fechaInicio) params.fechaInicio = fechaInicio;
        if (fechaFin) params.fechaFin = fechaFin;
        if (fechaCriterio) params.fechaCriterio = fechaCriterio;
      } else if (catalogo === 'Agendas') {
        if (fechaInicio) params.fechaInicio = fechaInicio;
        if (fechaFin) params.fechaFin = fechaFin;
      }

      // Agregar filtros adicionales
      if (filtroCriterio1 && filtroValor1) {
        params.filtroCriterio1 = filtroCriterio1;
        params.filtroValor1 = filtroValor1;
      }
      if (filtroCriterio2 && filtroValor2) {
        params.filtroCriterio2 = filtroCriterio2;
        params.filtroValor2 = filtroValor2;
      }
      if (filtroCriterio3 && filtroValor3) {
        params.filtroCriterio3 = filtroCriterio3;
        params.filtroValor3 = filtroValor3;
      }

      const blob = await reporteTextoService.generarReporte(params);
      reporteTextoService.descargarCSV(blob, 'ReporteTexto.csv');
    } catch (err: any) {
      setError(err.message || 'Error al generar reporte');
    } finally {
      setIsLoading(false);
    }
  }, [
    catalogo,
    camposSeleccionados,
    construirCamposSolicitados,
    user,
    fechaCriterio,
    fechaInicio,
    fechaFin,
    filtroCriterio1,
    filtroValor1,
    filtroCriterio2,
    filtroValor2,
    filtroCriterio3,
    filtroValor3,
  ]);

  // Resetear campos seleccionados al cambiar catálogo
  React.useEffect(() => {
    setCamposSeleccionados([]);
  }, [catalogo]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reporte de Texto</h1>
          <p className="text-sm text-gray-500 mt-1">
            Genera reportes personalizados en CSV seleccionando campos de Solicitudes, Empresas o Agendas
          </p>
        </div>
        <Button 
          variant="primary" 
          onClick={generarReporte}
          disabled={isLoading || camposSeleccionados.length === 0}
        >
          {isLoading ? 'Generando...' : 'Descargar CSV'}
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Selección de catálogo */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Seleccionar Catálogo</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(['Solicitudes', 'Empresas', 'Agendas'] as Catalogo[]).map((cat) => (
            <button
              key={cat}
              onClick={() => setCatalogo(cat)}
              className={`p-4 rounded-lg border-2 transition-all ${
                catalogo === cat
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-semibold">{cat}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Filtros de fecha (solo para Solicitudes y Agendas) */}
      {(catalogo === 'Solicitudes' || catalogo === 'Agendas') && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Filtros de Fecha</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {catalogo === 'Solicitudes' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campo Base para Fecha
                </label>
                <select
                  value={fechaCriterio}
                  onChange={(e) => setFechaCriterio(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {camposFechaSolicitudes.map(campo => (
                    <option key={campo.key} value={campo.key}>
                      {campo.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Input
              label="Fecha Inicio"
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
            <Input
              label="Fecha Fin"
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Filtros adicionales */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Filtros Adicionales</h2>
        <div className="space-y-4">
          {/* Filtro 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Criterio 1
              </label>
              <select
                value={filtroCriterio1}
                onChange={(e) => setFiltroCriterio1(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar...</option>
                {camposFiltro.map(campo => (
                  <option key={campo.key} value={campo.key}>
                    {campo.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Valor 1"
              value={filtroValor1}
              onChange={(e) => setFiltroValor1(e.target.value)}
            />
          </div>
          
          {/* Filtro 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Criterio 2
              </label>
              <select
                value={filtroCriterio2}
                onChange={(e) => setFiltroCriterio2(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar...</option>
                {camposFiltro.map(campo => (
                  <option key={campo.key} value={campo.key}>
                    {campo.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Valor 2"
              value={filtroValor2}
              onChange={(e) => setFiltroValor2(e.target.value)}
            />
          </div>
          
          {/* Filtro 3 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Criterio 3
              </label>
              <select
                value={filtroCriterio3}
                onChange={(e) => setFiltroCriterio3(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar...</option>
                {camposFiltro.map(campo => (
                  <option key={campo.key} value={campo.key}>
                    {campo.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Valor 3"
              value={filtroValor3}
              onChange={(e) => setFiltroValor3(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Selección de campos */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Seleccionar Campos ({camposSeleccionados.length} seleccionados)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
          {camposDisponibles.map((campo) => (
            <label
              key={campo.key}
              className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={camposSeleccionados.includes(campo.key)}
                onChange={() => toggleCampo(campo.key)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{campo.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReporteTextoManagement;
